import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/i18n";
import CrewList from "@/components/CrewList";
import { fmin, useFormat } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { ReadOnlyBanner } from "@/components/ReadOnly";
import {
  fetchSupervisor, fetchHistory, fetchFitterStats, fetchDayTimeline, type SupervisorJob, type HistoryJob, type FitterPeriod,
} from "@/services/preparation";

// The team leader's tab. It reports; it never touches a job.
//
// His rules (`renderBoss`): the rate shows on every job that has items to measure, paused or just
// started; only the ALERTS wait for 30 minutes of a running job. A job with no item says it cannot be
// measured. `onOpenJob` is absent for someone without the Assembly module: no "Open the job" then.
//
// ⛔ EVERY QUERY HERE REFRESHES EVERY 30 s, IN THE BACKGROUND TOO, as his `setInterval` does: the board
// is left open on a screen in the office, and a tab that stopped refreshing would still look current.
const LIVE = { refetchInterval: 30_000, refetchIntervalInBackground: true } as const;

type OpenJob = ((eventId: number) => void) | undefined;

export default function SupervisorPage({ warehouseId, onOpenJob }: { warehouseId: number | null; onOpenJob?: (eventId: number) => void }) {
  const { t } = useT();
  const [view, setView] = useState<"live" | "past">("live");
  // His "Ouvrir la fiche" lands on the job from its top, wherever the board was scrolled.
  const open: OpenJob = onOpenJob && ((eventId) => { window.scrollTo({ top: 0 }); onOpenJob(eventId); });

  return (
    <div className="space-y-5">
      <ReadOnlyBanner module="supervisor" text={t("sup.readOnly")} />
      <div className="flex gap-1">
        {(["live", "past"] as const).map((v) => (
          <button key={v} onClick={() => setView(v)}
            className={`rounded-lg px-3 py-1 text-sm transition ${
              view === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {t(v === "live" ? "hist.live" : "hist.past")}
          </button>
        ))}
      </div>
      {view === "live" ? <LiveBoard warehouseId={warehouseId} onOpenJob={open} /> : <PastBoard warehouseId={warehouseId} />}
      <CrewList warehouseId={warehouseId} />
    </div>
  );
}

function LiveBoard({ warehouseId, onOpenJob }: { warehouseId: number | null; onOpenJob: OpenJob }) {
  const { t } = useT();
  const { staff } = useAuth();
  // Wall time and idle time move on their own, so the page has to as well.
  const q = useQuery({ queryKey: ["supervisor", warehouseId], queryFn: () => fetchSupervisor(warehouseId), ...LIVE });
  // His "today's rate": everything earned today ÷ every fitter's minutes today.
  const day = useQuery({ queryKey: ["fitter-stats", warehouseId, "today"], queryFn: () => fetchFitterStats(warehouseId, "today"), ...LIVE });
  const dayTotal = day.data?.total;

  if (q.isPending) return <p className="text-sm text-slate-400">{t("loading")}</p>;
  if (q.isError) return <p className="text-sm text-red-700">{(q.error as Error).message}</p>;

  const { jobs, totals } = q.data!;
  const alerts = jobs.flatMap((job) => job.productivity.alerts.map((a) => ({ job, a })));

  return (
    <div className="space-y-5">
      {/* His note: how the rate is counted and who is told. */}
      <p className="text-xs text-slate-500">{t("sup.note")}</p>
      <div className="flex flex-wrap gap-3">
        <Total label={t("sup.running")} value={totals.running} />
        <Total label={t("sup.people")} value={totals.people} />
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-slate-800">
          <p className="text-xl font-semibold tabular-nums"><Rate value={dayTotal?.rate ?? null} /></p>
          <p className="text-xs opacity-70">
            {t("sup.dayRate")}
            {dayTotal && dayTotal.minutes > 0 && <span className="ml-1 tabular-nums">· {fmin(dayTotal.earned)} / {fmin(dayTotal.minutes)}</span>}
          </p>
        </div>
        <Total label={t("sup.kAlerts")} value={totals.alerts} tone={totals.alerts ? "red" : undefined} />
      </div>

      {/* His "Alertes": one row per alert — the client, where, who, why, and whether Slack has gone. */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">{t("sup.alT")}</h3>
        {alerts.length === 0 && <p className="text-xs text-slate-500">{t("sup.noAl")}</p>}
        {alerts.map(({ job, a }) => <AlertRow key={`${job.run.id}-${a.kind}`} job={job} a={a} onOpenJob={onOpenJob} />)}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">{t("sup.live")}</h3>
        {jobs.length === 0 && <p className="text-sm text-slate-400">{t("sup.none")}</p>}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {/* His order: jobs with alerts first, then running before paused. */}
          {[...jobs].sort((a, b) => b.productivity.alerts.length - a.productivity.alerts.length || (a.run.status === "running" ? -1 : 1))
            .map((job) => <JobRow key={job.run.id} job={job} onOpen={onOpenJob && (() => onOpenJob(job.event.id))} />)}
        </div>
      </section>

      <DayTimelineBoard warehouseId={warehouseId} />

      {staff?.is_admin && <PerFitter warehouseId={warehouseId} />}
    </div>
  );
}

const jobTitle = (job: SupervisorJob) => job.event.client_name || `#${job.event.number ?? job.event.id}`;

function AlertRow({ job, a, onOpenJob }: { job: SupervisorJob; a: SupervisorJob["productivity"]["alerts"][number]; onOpenJob: OpenJob }) {
  const { t } = useT();
  const { zurichTime } = useFormat();
  // The hourly check runs on the warehouses' clock, so its time is said on it too.
  const sent = job.alerts_sent?.[a.kind];
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 rounded-xl border border-red-300 bg-red-50 px-3 py-2.5">
      <div className="min-w-0">
        <b className="text-[15px] text-slate-900">⚠️ {jobTitle(job)}</b>{" "}
        <span className="text-xs text-slate-600">{job.event.warehouse?.name ?? ""} · 👷 {job.team.join(", ")}</span>
        <p className="text-sm font-semibold text-slate-900">
          {a.kind === "yield"
            ? t("sup.alertYield", { p: Math.round(a.value * 100), l: Math.round(a.limit * 100) })
            : t("sup.alertIdle", { m: fmin(a.value), l: fmin(a.limit) })}
        </p>
        <p className="text-xs text-slate-600">{sent ? t("sup.sentAt", { t: zurichTime(new Date(sent).getTime()) }) : t("sup.sentWait")}</p>
      </div>
      {onOpenJob && (
        <button onClick={() => onOpenJob(job.event.id)} className="rounded-lg border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50">
          {t("sup.open")}
        </button>
      )}
    </div>
  );
}

// His colours for the jobs of the day, handed out in his job order (the server sends the jobs in it).
const JOB_COLOURS = ["#2a78d6", "#eb6834", "#1baf7a", "#c98500", "#d55181", "#7b6fd6", "#0f9fb3", "#8c6d3f"];
const HOUR = 3_600_000;

// His "Journée minute par minute": a row per fitter, a block per stretch on a job, a white mark per
// validated piece, the hours across the top and a red line at now. Hover a block or a mark for details.
// The axis is the warehouses' day, so its hovers are on the warehouses' clock too (Zurich): a block at
// 9 h on the axis must not say 13:30 to a reader in India.
function DayTimelineBoard({ warehouseId }: { warehouseId: number | null }) {
  const { t, locale } = useT();
  const { zurichTime } = useFormat();
  const q = useQuery({ queryKey: ["day-timeline", warehouseId], queryFn: () => fetchDayTimeline(warehouseId), ...LIVE });
  const d = q.data;
  const head = (
    <>
      <h3 className="px-4 pt-3 text-sm font-semibold text-slate-900">{t("sup.tl")}</h3>
      <p className="px-4 pb-2 text-xs text-slate-500">{t("sup.tlN")}</p>
    </>
  );
  if (!d || d.rows.length === 0) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white pb-3">
        {head}
        <p className="px-4 text-xs text-slate-400">{q.isPending ? t("loading") : q.isError ? (q.error as Error).message : t("sup.noTl")}</p>
      </section>
    );
  }

  const ms = (iso: string) => new Date(iso).getTime();
  const from = ms(d.from), now = ms(d.now);
  const first = Math.min(...d.rows.flatMap((r) => r.blocks.map((b) => ms(b.from))));
  // His axis: from the first hour worked (7 h at the latest) to now, four hours at the least.
  const t0 = Math.min(Math.floor(first / HOUR) * HOUR, from + 7 * HOUR);
  const t1 = Math.max(now, t0 + 4 * HOUR);
  const pc = (v: number) => ((v - t0) / (t1 - t0)) * 100;
  const hours: number[] = [];
  for (let h = Math.ceil(t0 / HOUR) * HOUR; h <= t1; h += HOUR) hours.push(h);
  const hourLabel = new Intl.DateTimeFormat(locale, { hour: "numeric", hourCycle: "h23", timeZone: "Europe/Zurich" });
  const jobs = new Map(d.jobs.map((j, i) => [j.event_id, { colour: JOB_COLOURS[i % JOB_COLOURS.length], name: j.client || `#${j.number ?? j.event_id}` }]));

  return (
    <section className="rounded-xl border border-slate-200 bg-white pb-3">
      {head}
      <div className="overflow-x-auto px-4">
        <div className="min-w-[480px] space-y-1">
          <div className="grid grid-cols-[110px_1fr] gap-2 text-[11px] text-slate-400">
            <span />
            <div className="relative h-4">
              {hours.map((h) => (
                <span key={h} className="absolute -translate-x-1/2 tabular-nums" style={{ left: `${pc(h)}%` }}>
                  {Number(hourLabel.format(h).replace(/\D/g, ""))}h
                </span>
              ))}
            </div>
          </div>
          {d.rows.map((r) => (
            <div key={r.name} className="grid grid-cols-[110px_1fr] items-center gap-2 text-xs">
              <span className="truncate text-slate-700">{r.name}</span>
              <div className="relative h-[22px] rounded bg-slate-100">
                {r.blocks.map((b, i) => {
                  const job = jobs.get(b.event_id);
                  const s = ms(b.from), e = ms(b.to);
                  return (
                    <span key={i} className="absolute bottom-0.5 top-0.5 rounded-[3px] opacity-90"
                      style={{ left: `${pc(s)}%`, width: `${Math.max(0.3, pc(e) - pc(s))}%`, background: job?.colour }}
                      title={`${job?.name ?? ""} · ${zurichTime(s)}–${zurichTime(e)} · ${fmin((e - s) / 60_000)}`} />
                  );
                })}
                {r.ticks.map((k, i) => (
                  <span key={`t${i}`} className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${pc(ms(k.at))}%` }}
                    title={[zurichTime(ms(k.at)), k.piece, jobs.get(k.event_id)?.name].filter(Boolean).join(" · ")} />
                ))}
                <span className="absolute -bottom-0.5 -top-0.5 w-0.5 bg-red-600" style={{ left: `${pc(now)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// His "Par monteur" (`fitStats`): each fitter's assembly time, the planned time of what they
// validated, the rate, the items (shared when several built one) and the alerts on their jobs.
function PerFitter({ warehouseId }: { warehouseId: number | null }) {
  const { t } = useT();
  const [period, setPeriod] = useState<FitterPeriod>("today");
  const q = useQuery({ queryKey: ["fitter-stats", warehouseId, period], queryFn: () => fetchFitterStats(warehouseId, period), ...LIVE });
  const rows = q.data?.fitters ?? [];
  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <h3 className="px-4 pt-3 text-sm font-semibold text-slate-900">{t("sup.per")}</h3>
      <p className="px-4 pb-2 text-xs text-slate-500">{t("sup.perN")}</p>
      <div className="flex gap-1 px-4 pb-3">
        {(["today", "7d", "30d"] as const).map((k) => (
          <button key={k} onClick={() => setPeriod(k)} aria-pressed={period === k}
            className={`rounded-lg px-3 py-1 text-xs transition ${
              period === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
            {t(`sup.period.${k}`)}
          </button>
        ))}
      </div>
      {q.isPending ? <p className="px-4 pb-3 text-xs text-slate-400">{t("loading")}</p>
        : q.isError ? <p className="px-4 pb-3 text-xs text-red-700">{(q.error as Error).message}</p>
        : rows.length === 0 ? <p className="px-4 pb-3 text-xs text-slate-400">{t("sup.perNone")}</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-y border-slate-200 text-slate-500">
                  <th className="px-3 py-2 text-left font-medium">{t("sup.col.fitter")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("sup.col.minutes")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("sup.col.earned")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("sup.col.rate")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("sup.col.items")}</th>
                  <th className="px-3 py-2 text-right font-medium">{t("sup.col.alerts")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.name} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2 text-slate-800">{f.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmin(f.minutes)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmin(f.earned)}</td>
                    <td className="px-3 py-2 text-right"><Rate value={f.rate} /></td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.items}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.alerts || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </section>
  );
}

// His `rdHtml`: the rate as a percentage on his three levels — good from 90 %, middle from 70 %,
// judged on the ratio itself (0.895 is middle, as the card's tint says); only the figure is rounded.
function Rate({ value }: { value: number | null }) {
  if (value === null) return <span className="text-slate-300">–</span>;
  const pct = Math.round(value * 100);
  const tone = value >= 0.9 ? "text-emerald-600" : value >= 0.7 ? "text-amber-600" : "text-red-600";
  return <span className={`font-semibold tabular-nums ${tone}`}>{pct} %</span>;
}

// His live card (`renderBoss`): the client and whether it runs, who and where, then spent · earned of
// the job's planning estimate · rate · last validation, the gauge (always drawn — 0 % without an
// estimate) and the sparkline. His tint: red edge on any alert, amber when the rate is under 90 %.
// Nothing else: his card has no "credited" line and no "next to build" line.
function JobRow({ job, onOpen }: { job: SupervisorJob; onOpen?: () => void }) {
  const { t } = useT();
  const p = job.productivity;
  const est = job.estimate_minutes ?? 0;
  const pct = est ? Math.min(100, Math.round((p.earned_minutes / est) * 100)) : 0;
  const tint = p.alerts.length ? "border-red-400 shadow-[inset_3px_0_0_#dc2626]"
    : p.yield_ratio !== null && p.yield_ratio < 0.9 ? "border-slate-200 shadow-[inset_3px_0_0_#d97706]" : "border-slate-200";

  return (
    <section className={`flex flex-col gap-1.5 rounded-xl border bg-white px-3 py-2.5 ${tint}`}>
      <header className="flex items-baseline justify-between gap-2">
        <b className="text-[15px] text-slate-900">{jobTitle(job)}</b>
        <span className="text-xs text-slate-500">{job.run.status === "running" ? t("sup.stRun") : t("sup.stPau")}</span>
      </header>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[13px] text-slate-600">
        <span>👷 {job.team.join(", ") || "–"}</span>
        <span>{job.event.warehouse?.name ?? ""}</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[13px] text-slate-600">
        <span>{t("sup.spent")} <b className="tabular-nums text-slate-900">{fmin(p.worked_minutes)}</b></span>
        <span>{t("sup.earned")} <b className="tabular-nums text-slate-900">{fmin(p.earned_minutes)}</b> / {fmin(est)}</span>
        <span>{t("sup.yield")} {p.measurable ? <Rate value={p.yield_ratio} /> : "–"}</span>
        <span>{job.validated ? t("sup.lastAgo", { t: fmin(p.idle_minutes) }) : t("sup.never")}</span>
      </div>
      {!p.measurable && <p className="text-[13px] text-slate-600">{t("sup.nm")}</p>}
      <div className="relative h-3 overflow-hidden rounded-md border border-slate-200 bg-slate-50" role="img" aria-label={`${pct} %`}>
        <div className="absolute inset-y-0 left-0 rounded-md bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
      {job.spark && job.spark.length > 1 && <Spark points={job.spark} />}

      {onOpen && (
        <div>
          <button onClick={onOpen} className="rounded-lg border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50">{t("sup.open")}</button>
        </div>
      )}
    </section>
  );
}

// His `sparkHtml`: spent (grey) and earned (green) since the start, on one scale.
function Spark({ points }: { points: [number, number][] }) {
  const { t } = useT();
  const W = 300, H = 54;
  const max = Math.max(1, ...points.map(([s, e]) => Math.max(s, e)));
  const line = (k: 0 | 1) => points.map((pt, i) => `${((i / (points.length - 1)) * W).toFixed(1)},${(H - 2 - (pt[k] / max) * (H - 4)).toFixed(1)}`).join(" ");
  return (
    <div className="mt-2">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-[54px] w-full" role="img"
        aria-label={`${t("sup.spkSpent")} / ${t("sup.spkEarned")}`}>
        <polyline points={line(0)} fill="none" className="stroke-slate-400" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        <polyline points={line(1)} fill="none" className="stroke-emerald-600" strokeWidth={2} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-1 flex gap-4 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><i className="inline-block h-0.5 w-3 bg-slate-400" />{t("sup.spkSpent")}</span>
        <span className="flex items-center gap-1"><i className="inline-block h-0.5 w-3 bg-emerald-600" />{t("sup.spkEarned")}</span>
      </div>
    </div>
  );
}

function Total({ label, value, tone }: { label: string; value: number; tone?: "amber" | "red" }) {
  const colour = tone === "red" ? "bg-red-50 text-red-800"
    : tone === "amber" ? "bg-amber-50 text-amber-900" : "bg-white text-slate-800";
  return (
    <div className={`rounded-xl border border-slate-200 px-4 py-2 ${colour}`}>
      <p className="text-xl font-semibold tabular-nums">{value}</p>
      <p className="text-xs opacity-70">{label}</p>
    </div>
  );
}


// ⛔ READS SNAPSHOTS, NOT THE FEED. These jobs are mostly older than the feed's ten-week window, so
// the estimate, the client and the warehouse are the ones written down on the day the job was done.
// His "Réel vs estimé" (`renderStats`): every finished job with an estimate, complete or not — four
// figures, the jobs one by one with each fitter's time and the gap, and — for admins — each fitter
// against their share of the estimate, over those same jobs.
export function PastBoard({ warehouseId }: { warehouseId: number | null }) {
  const { t } = useT();
  const { staff } = useAuth();
  const q = useQuery({ queryKey: ["history", warehouseId], queryFn: () => fetchHistory(warehouseId) });

  if (q.isPending) return <p className="text-sm text-slate-400">{t("loading")}</p>;
  if (q.isError) return <p className="text-sm text-red-700">{(q.error as Error).message}</p>;
  const { jobs, summary } = q.data!;
  if (!jobs.length) return <p className="text-sm text-slate-400">{t("hist.none")}</p>;

  // His per-fitter table: over the listed jobs, a fitter's minutes against the job's estimate times
  // their share of the job's minutes.
  const fitters = new Map<string, { n: number; e: number; r: number }>();
  for (const j of jobs) {
    const total = Object.values(j.minutes_by_person).reduce((a, b) => a + b, 0) || 1;
    for (const [name, min] of Object.entries(j.minutes_by_person)) {
      const o = fitters.get(name) ?? { n: 0, e: 0, r: 0 };
      o.n += 1; o.r += min; o.e += (j.estimated_minutes * min) / total;
      fitters.set(name, o);
    }
  }
  const perFitter = [...fitters.entries()].sort((a, b) => b[1].n - a[1].n || a[0].localeCompare(b[0]));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Kpi label={t("hist.kpiJobs")} value={String(summary.jobs)} />
        <Kpi label={t("hist.kpiActual")} value={fmin(summary.worked_minutes)} hint={t("hist.ofEst", { e: fmin(summary.estimated_minutes) })} />
        <Kpi label={t("hist.kpiGap")} value={<Gap value={summary.gap} />} />
        <Kpi label={t("hist.kpiOk")} value={`${summary.within_estimate}/${summary.jobs}`} />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white">
        <h3 className="px-4 pt-3 text-sm font-semibold text-slate-900">{t("hist.perJob")}</h3>
        <p className="px-4 pb-2 text-xs text-slate-500">{t("hist.statsNote")}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-y border-slate-200 text-slate-500">
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">{t("hist.when")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.colNo")}</th>
                <th className="px-3 py-2 text-left font-medium">{t("hist.colClient")}</th>
                {warehouseId === null && <th className="whitespace-nowrap px-3 py-2 text-left font-medium">{t("hist.colWh")}</th>}
                <th className="px-3 py-2 text-left font-medium">{t("hist.colFitters")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.estimate")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.actual")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.gap")}</th>
              </tr>
            </thead>
            <tbody>{jobs.map((j) => <PastRow key={j.run_id} job={j} showWarehouse={warehouseId === null} />)}</tbody>
          </table>
        </div>
      </section>

      {staff?.is_admin && perFitter.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white">
          <h3 className="px-4 pt-3 text-sm font-semibold text-slate-900">{t("hist.perFitter")}</h3>
          <p className="px-4 pb-2 text-xs text-slate-500">{t("hist.perFitterNote")}</p>
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-y border-slate-200 text-slate-500">
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium">{t("hist.colFitters")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.colJobs")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.estimate")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.actual")}</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">{t("hist.colVs")}</th>
              </tr>
            </thead>
            <tbody>
              {perFitter.map(([name, o]) => (
                <tr key={name} className="border-b border-slate-100 last:border-0">
                  <td className="whitespace-nowrap px-3 py-2 text-slate-800">{name}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{o.n}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{fmin(o.e)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{fmin(o.r)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right"><Gap value={(o.r - o.e) / (o.e || 1)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

// His row: "Fri 30.10" (`dayLbl`), and no value ever splits across two lines — his `th,td` are
// nowrap, only the client and the fitters (`td.w`) wrap.
function PastRow({ job, showWarehouse }: { job: HistoryJob; showWarehouse: boolean }) {
  const { dayLbl } = useFormat();
  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="whitespace-nowrap px-3 py-2 text-slate-600">{dayLbl(job.finished_at)}</td>
      {/* His `numTxt`: the planning entry's number, nothing when it has none. */}
      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-500">{job.number != null ? `#${job.number}` : ""}</td>
      <td className="min-w-[180px] px-3 py-2 font-medium text-slate-800">{job.client_name || "—"}</td>
      {showWarehouse && <td className="whitespace-nowrap px-3 py-2 text-slate-500">{job.warehouse_name ?? ""}</td>}
      <td className="min-w-[180px] px-3 py-2 text-slate-600">{Object.entries(job.minutes_by_person).map(([n, m]) => `${n} ${fmin(m)}`).join(", ") || "—"}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">{fmin(job.estimated_minutes)}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">{fmin(job.worked_minutes)}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right"><Gap value={job.gap} /></td>
    </tr>
  );
}

// His `dv`: "+12 %" — red above +5 %, green below −5 %; a job we cannot compare says "—".
function Gap({ value }: { value: number | null }) {
  if (value === null) return <span className="text-slate-300">—</span>;
  const pct = Math.round(value * 100);
  const tone = value > 0.05 ? "text-red-600" : value < -0.05 ? "text-emerald-600" : "text-slate-700";
  return <span className={`font-semibold tabular-nums ${tone}`}>{pct > 0 ? "+" : ""}{pct} %</span>;
}

